'use client';
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useQueryState,
  parseAsStringLiteral,
  type UseQueryStateReturn,
  Options as QueryStateOptions,
  parseAsBoolean,
} from 'nuqs';
import { useTsr } from '@/api/tsr';
import { useGlobalAccountContext } from '@/components/AccountPicker';
import { useIsToroExtension } from '@repo/ui/components/ToroExtensionContext';
import { useSearchParams } from 'next/navigation';
import { CandidateInsightsProfilePromptType } from '@repo/common/schemas/llm';
import { DateFilterValue } from '@/components/DatePicker';
import * as R from 'remeda';
import { BullhornField } from '@repo/common/interfaces';
import { BullhornApiMeta } from '@repo/common/schemas/external/bullhorn';
import {
  AccountDTO,
  DataIqCheckForUpdateOptions,
  LlmPromptJsonData,
} from '@repo/common/schemas/models';
import { skipToken, useMutation, useQuery, useQueryClient, useTRPC } from '@/api/trpc';
import {
  ReferenceFieldOption,
  SelectedField,
} from '@/components/data-iq/eval/SelectedFieldsContext';
import { type FieldFilterOperation, ObjectIdString, SearchEntityQuery } from '@repo/common/schemas';
import z from 'zod';
import { ToastContext } from '@/components/Toasts';
import { copyElement } from '@/utils';
import { PopulatedAccountDTO } from '@repo/common/schemas/api';

export { CandidateProfilePromptType } from '@repo/common/schemas/llm';

export function formatSelectedFieldForQuery(field: SelectedField) {
  return {
    name: field.name,
    status: field.status || 'Set', // Default to 'Set' if null
    values:
      field.selectedOptions && field.selectedOptions.length > 0 ?
        field.selectedOptions // Send full {value, label} objects for reference fields
      : field.values, // Keep raw values for non-reference fields
    operator: field.operator,
    excludedValues: field.excludedValues,
    label: field.label || field.name, // Use name as fallback for label
  };
}

export function useAllAccounts(isGlobal?: boolean) {
  const trpc = useTRPC();
  const { isInternalUser, selectedAccount } = useGlobalAccountContext();
  const { data, error, isLoading } = useQuery(trpc.accounts.list.queryOptions({}));

  // Apply global account override if applicable
  const allAccounts: PopulatedAccountDTO[] = useMemo(() => {
    const actualAllAccounts = data ?? [];
    if (isGlobal) {
      return actualAllAccounts;
    }
    if (!isInternalUser && selectedAccount) {
      return actualAllAccounts.filter(({ _id }) => _id === selectedAccount._id);
    }
    return actualAllAccounts;
  }, [data, isInternalUser, selectedAccount, isGlobal]);

  // Filtered list of accounts with bullhorn integrations
  const bullhornAccounts = useMemo(
    () => allAccounts.filter((account) => account.primaryIntegration?.type === 'bullhorn'),
    [allAccounts]
  );

  return {
    allAccounts,
    bullhornAccounts,
    isLoading,
    isError: error,
  };
}

export function useQueryStateAccount() {
  const { allAccounts, isLoading } = useAllAccounts();
  return useQueryStateAccountInternal(allAccounts, isLoading);
}

export function useBullhornQueryStateAccount() {
  const { bullhornAccounts, isLoading } = useAllAccounts();
  return useQueryStateAccountInternal(bullhornAccounts, isLoading);
}

function useQueryStateAccountInternal(
  accountList: AccountDTO[],
  isLoading = false
): [AccountDTO | null, (account?: AccountDTO | string | null) => Promise<void>] {
  const { selectedAccount: globalAccount, isInternalUser } = useGlobalAccountContext();
  const [accountId, setAccountId] = useQueryState('account');
  const setAccount = useCallback(
    async (account?: AccountDTO | string | null) => {
      if (globalAccount) {
        return;
      }
      const id = R.isString(account) ? account : account?._id;
      await setAccountId(id || null);
    },
    [setAccountId, globalAccount]
  );

  const account = useMemo(() => {
    if (globalAccount && !isInternalUser) {
      return globalAccount;
    }
    if (!accountId) return null;

    let account = accountList.find(({ _id }) => _id === accountId);
    if (isLoading && !account) {
      // Use a placeholder but only while loading
      account = AccountDTO.safeParse({ _id: accountId, name: accountId }).data;
    }
    return account ?? null;
  }, [accountId, accountList, globalAccount, isInternalUser, isLoading]);

  return [account, setAccount];
}

export function useAllPrompts() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const {
    data: allPrompts,
    isLoading,
    error,
    refetch,
  } = useQuery(trpc.llmPrompt.getAll.queryOptions());

  const { mutateAsync, isPending: isSavingPrompt } = useMutation(
    trpc.llmPrompt.save.mutationOptions({
      onSuccess: () => queryClient.invalidateQueries(trpc.llmPrompt.getAll.queryFilter()),
    })
  );

  // expectedUpdatedAt is the optimistic-concurrency check for an in-place draft edit.
  const savePrompt = useCallback(
    (prompt: LlmPromptJsonData, previousPromptId?: string, expectedUpdatedAt?: Date) =>
      mutateAsync({ prompt, previousPromptId, expectedUpdatedAt }),
    [mutateAsync]
  );

  // Input type comes straight from the router (draft excluded); no wrapper needed.
  const { mutateAsync: setPromptStatus, isPending: isSettingStatus } = useMutation(
    trpc.llmPrompt.setStatus.mutationOptions({
      onSuccess: () => queryClient.invalidateQueries(trpc.llmPrompt.getAll.queryFilter()),
    })
  );

  // Hits the server every time so callers decide on fresh rows, not the hour-old cache.
  const refetchPrompts = useCallback(
    async () => (await refetch({ throwOnError: true })).data ?? [],
    [refetch]
  );
  // Fire-and-forget refresh for callers that only need the cache updated.
  const refreshPrompts = useCallback(() => void refetch(), [refetch]);

  const [validPrompts, invalidPrompts] = useMemo(() => {
    return R.partition(allPrompts ?? [], (prompt) => !prompt._isInvalid);
  }, [allPrompts]);

  return {
    // Valid save prompts
    validPrompts,
    // Invalid saved prompts
    invalidPrompts,
    // All saved prompts, including invalid ones
    allPrompts,
    isLoading,
    error,
    savePrompt,
    isSavingPrompt,
    refetchPrompts,
    setPromptStatus,
    isSettingStatus,
    refreshPrompts,
  };
}

type ZodEnumFor<T extends string> = z.ZodEnum<Readonly<Record<T, T>>>;

type WrappedQueryStateReturn<T extends string, Default extends T | undefined> = [
  UseQueryStateReturn<T, Default>[0],
  Dispatch<SetStateAction<string | null>>,
];

export function useEnumQueryState<T extends string>(
  key: string,
  schema: ZodEnumFor<T>,
  options?: QueryStateOptions
): WrappedQueryStateReturn<T, undefined>;
export function useEnumQueryState<T extends string>(
  key: string,
  schema: z.ZodDefault<ZodEnumFor<T>>,
  options?: QueryStateOptions
): WrappedQueryStateReturn<T, T>;
export function useEnumQueryState<T extends string>(
  key: string,
  schema: ZodEnumFor<T> | z.ZodDefault<ZodEnumFor<T>>,
  options: QueryStateOptions = {}
) {
  const zodEnum = 'options' in schema ? schema : schema.unwrap();

  const defaultValue = 'defaultValue' in schema.def ? schema.def.defaultValue : undefined;
  if (defaultValue) {
    // If we have a defaultValue, set `clearOnDefault` to true unless specified otherwise
    options.clearOnDefault ??= true;
  }
  let parser = parseAsStringLiteral(zodEnum.options as T[]).withOptions(options);

  if (defaultValue) {
    parser = parser.withDefault(defaultValue);
  }
  const [value, setValue] = useQueryState<T>(key, parser);
  const wrappedSetValue = useCallback<Dispatch<SetStateAction<string | null>>>(
    (val) => {
      setValue((prev) => {
        const raw = R.isFunction(val) ? val(prev) : val;
        if (R.isNullish(raw)) {
          return null;
        }
        const parsed = zodEnum.safeParse(raw);
        if (parsed.success) {
          return parsed.data;
        }

        return defaultValue ?? null;
      });
    },
    [defaultValue, setValue, zodEnum]
  );
  return [value, wrappedSetValue];
}

export function useBooleanQueryState(key: string) {
  return useQueryState(
    key,
    parseAsBoolean.withOptions({ clearOnDefault: true }).withDefault(false)
  );
}

export function useDebounceState<T = undefined>(
  initialState: T | (() => T),
  delay: number = 100
): [T, T, Dispatch<SetStateAction<T>>] {
  const [actualValue, setActualValue] = useState<T>(initialState);
  const [tempValue, setTempValue] = useState<T>(initialState);
  const debounceUpdate = useMemo(
    () =>
      R.funnel(setActualValue, {
        minQuietPeriodMs: delay,
        // Reducer is needed to allow arguments to be passed in
        reducer: (_prev, val: SetStateAction<T>) => val,
      }),
    [delay]
  );

  const setValue: Dispatch<SetStateAction<T>> = useCallback(
    (val: SetStateAction<T>) => {
      setTempValue(val);
      debounceUpdate.call(val);
    },
    [debounceUpdate]
  );

  return [actualValue, tempValue, setValue];
}

// Returns searchParams with an added delay to prevent too many updates when a user is typing
export function useDebounceSearchParams(delay = 500) {
  const actualSearchParams = useSearchParams();
  const [debouncedValue, tempValue, setDebouncedValue] = useDebounceState(
    actualSearchParams,
    delay
  );
  useEffect(() => {
    setDebouncedValue(actualSearchParams);
  }, [actualSearchParams, setDebouncedValue]);
  return debouncedValue;
}

/**
 * Takes a callback and a delay calculator, and returns a dynamically debounced function. Delay is recalculated after every successful execution
 * @param callback - The function to execute after a delay
 * @param calculateDelay - Function to calculate the delay. Expects the same arguments as the main callback function.
 * @param initialDelay - Initial delay value in milliseconds. Defaults to 500
 */
export function useDynamicDebounce<F extends (...args: never[]) => void>(
  callback: F,
  calculateDelay: (...args: Parameters<NoInfer<F>>) => number,
  initialDelay: number
) {
  const [nextArgs, setNextArgs] = useState<Parameters<F> | null>(null);

  const delay: number = useMemo(() => {
    return nextArgs ? calculateDelay(...nextArgs) : initialDelay;
  }, [calculateDelay, nextArgs, initialDelay]);

  // Execute the callback with the latest args in an effect
  useEffect(() => {
    if (nextArgs) {
      const timeout = setTimeout(() => callback(...nextArgs), delay);
      return () => clearTimeout(timeout);
    }
  }, [nextArgs, callback, delay]);

  return useCallback((...args: Parameters<F>) => setNextArgs(args), []);
}

// Ask Toro prompt suggestions the account's TalentIQ config opts out of, by `shortPrompt` label
export function useHiddenPromptSuggestions() {
  const trpc = useTRPC();
  const { selectedAccount } = useGlobalAccountContext();
  const accountId = selectedAccount?._id;
  // TalentIQ config only applies to the card; the extension keeps the full prompt list
  const isToroExtension = useIsToroExtension();

  const { data, isLoading } = useQuery(
    trpc.talentIq.getHiddenPromptSuggestions.queryOptions(
      accountId && !isToroExtension ? { accountId } : skipToken
    )
  );

  // isLoading (not isPending) so a skipped query doesn't read as loading forever
  return { hiddenPromptSuggestions: data, isLoading };
}

export function useTalentIqPromptType(
  privateLabel?: string | null,
  promptTypeInput?: string | null
) {
  const trpc = useTRPC();
  const { selectedAccount } = useGlobalAccountContext();
  const accountId = selectedAccount?._id || null;

  // If a valid promptType was passed in, use that instead of sending a request to the backend
  const parsedPromptType = CandidateInsightsProfilePromptType.safeParse(promptTypeInput)?.data;

  const { data, error, isLoading } = useQuery(
    trpc.talentIq.getConfig.queryOptions(
      accountId && privateLabel && !parsedPromptType ? { accountId, privateLabel } : skipToken
    )
  );

  if (parsedPromptType) {
    return {
      promptType: parsedPromptType,
      isLoading: false,
      error: null,
    };
  }

  return {
    promptType: data || 'candidate_profile',
    isLoading,
    error,
  };
}

export function useBullhornFields(entity: string, accountId?: string | null) {
  const tsr = useTsr();
  const {
    data,
    isLoading,
    error: errorResponse,
  } = tsr.adminApi.getBullhornMeta.useQuery({
    queryKey: ['bullhornMeta', accountId, entity],
    queryData: {
      params: {
        accountId: accountId ?? '',
        entity,
      },
    },
    enabled: !!accountId,
  });
  const rawFields = data?.body?.fields ?? null;
  const fields = useMemo(() => {
    if (!rawFields) return null;

    // Find the index of dateAdded field
    const dateAddedIndex = rawFields.findIndex((field) => field.name === 'dateAdded');

    // If dateAdded field exists in the array
    if (dateAddedIndex !== -1) {
      // Create a copy of the array
      const sortedFields = [...rawFields];

      // Remove the dateAdded field from its current position
      const dateAddedField = sortedFields.splice(dateAddedIndex, 1)[0];

      // Add it to the beginning of the array
      sortedFields.unshift(dateAddedField);

      return sortedFields;
    }

    // If dateAdded field doesn't exist, return the original array
    return rawFields;
  }, [rawFields]);

  const error =
    errorResponse instanceof Error ? errorResponse.message : errorResponse?.status?.toString();
  return { fields, isLoading, error };
}

export function useCandidateQuery(
  account: AccountDTO | null,
  selectedFields: SelectedField[],
  dateFilters?: DateFilterValue[]
) {
  const tsr = useTsr();

  const accountId = account?._id;

  const searchEntityQuery = useMemo(() => {
    const fieldFilters = selectedFields
      .map(formatSelectedFieldForQuery)
      .filter((field) => field.status !== null);

    // Convert dateFilters object to array
    const dateFiltersArray = dateFilters?.filter((filter) => filter !== null) || [];

    return {
      fieldFilters,
      dateFilters: dateFiltersArray.length > 0 ? dateFiltersArray : undefined, // Keep for backwards compatibility
      fields: 'id,name',
      count: 30,
      showTotalMatched: true,
    };
  }, [selectedFields, dateFilters]);

  const { data, isLoading, error, refetch } = tsr.adminApi.searchEntity.useQuery({
    queryKey: ['searchCandidates', accountId, searchEntityQuery],
    queryData: {
      params: {
        accountId: accountId || '',
        entity: 'Candidate',
      },
      query: searchEntityQuery,
    },
    enabled: !!accountId,
  });

  return {
    numCandidates: data?.body.total || 0,
    candidates: data?.body.data || [],
    isLoading,
    error:
      error ?
        error instanceof Error ?
          error.message
        : String(error)
      : null,
    searchCandidates: refetch,
  };
}

export function useDataIqBatch(account?: AccountDTO | null) {
  const trpc = useTRPC();

  const accountId = account?._id;

  const { mutateAsync, isPending } = useMutation(trpc.dataIq.eval.startBatch.mutationOptions());
  const { mutate: endBatch } = useMutation(trpc.dataIq.eval.endBatch.mutationOptions());

  const runBatch = useCallback(
    (searchQuery: z.input<typeof SearchEntityQuery>, options: DataIqCheckForUpdateOptions) => {
      if (!accountId) return;

      // TODO: make these options overridable
      return mutateAsync({
        account: accountId,
        searchQuery,
        checkForUpdateOptions: options,
      });
    },
    [accountId, mutateAsync]
  );

  return useMemo(
    () => ({
      isPending,
      runBatch,
      endBatch,
    }),
    [endBatch, isPending, runBatch]
  );
}

export function useReferenceFieldOptions(
  accountOrId: AccountDTO | string | null,
  entity: string,
  field: string
) {
  const trpc = useTRPC();
  const accountId = typeof accountOrId === 'string' ? accountOrId : accountOrId?._id;
  const {
    data: options,
    isLoading,
    error,
  } = useQuery(
    trpc.bullhorn.getReferenceFieldOptions.queryOptions(
      accountId ? { accountId, entity, field } : skipToken
    )
  );
  const labels = options?.map(R.prop('label')) ?? [];
  return { options: options ?? [], labels, isLoading, error };
}

export function fieldHasEmbeddedOptions(
  field: BullhornField
): field is BullhornField & { options: ReferenceFieldOption[] } {
  return (
    'inputType' in field &&
    field.inputType === 'SELECT' &&
    'options' in field &&
    Array.isArray(field.options) &&
    field.options.length > 0
  );
}

export function getExcludedValues(
  selectedValues: (string | number)[],
  allOptions: ReferenceFieldOption[]
): (string | number)[] {
  return R.difference(
    allOptions.map((opt) => String(opt.value)),
    selectedValues.map(String)
  );
}

export function useReferenceFieldController(
  field: BullhornField,
  entity = 'Candidate',
  initialSelectedOptions?: ReferenceFieldOption[],
  initialOperator?: FieldFilterOperation
) {
  const [account] = useBullhornQueryStateAccount();

  const hasEmbedded = fieldHasEmbeddedOptions(field);

  const {
    options: fetchedOptions,
    isLoading,
    error,
  } = useReferenceFieldOptions(hasEmbedded ? null : account, entity, field.name);

  const options = hasEmbedded ? field.options : fetchedOptions;

  const [selectedOptions, setSelectedOptions] = useState<ReferenceFieldOption[]>(
    initialSelectedOptions || []
  );
  const [operator, setOperator] = useState<FieldFilterOperation>(initialOperator || 'OR');
  const [searchText, setSearchText] = useState('');

  const showSearch = (options?.length || 0) > 15;
  const filteredOptions = useMemo(() => {
    if (!showSearch) return options || [];
    if (!searchText.trim()) return options || [];

    return (options || []).filter(
      (option: ReferenceFieldOption) =>
        option.label.toLowerCase().includes(searchText.toLowerCase()) ||
        option.value.toString().toLowerCase().includes(searchText.toLowerCase())
    );
  }, [showSearch, options, searchText]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const clearSearch = () => {
    setSearchText('');
  };

  const handleToggleOption = (option: ReferenceFieldOption) => {
    setSelectedOptions((prev) =>
      prev.some((prevOption) => prevOption.value === option.value) ?
        prev.filter((prevOption) => prevOption.value !== option.value)
      : [...prev, option]
    );
  };

  const handleOperatorChange = (selectedOperator: FieldFilterOperation) => {
    setOperator(selectedOperator);
  };

  return {
    selectedOptions,
    operator,
    searchText,
    handleSearchChange,
    clearSearch,
    handleToggleOption,
    handleOperatorChange,
    options,
    filteredOptions,
    showSearch,
    isLoadingOptions: isLoading,
    isSingleValueField: !BullhornApiMeta.isArrayField(field),
  };
}

export function useNoteActions(integrationId: ObjectIdString) {
  const trpc = useTRPC();

  const {
    data: actions,
    isLoading,
    error,
  } = useQuery(trpc.configs.staffIq.getNoteActions.queryOptions({ integrationId }));

  return {
    actions: actions ?? [],
    isLoading,
    error,
  };
}

export function usePlacementFields(integrationId: ObjectIdString) {
  const trpc = useTRPC();

  const { data: fields } = useQuery(
    trpc.configs.staffIq.getPlacementFields.queryOptions({ integrationId })
  );

  return fields ?? [];
}

export function useCandidateStatuses(integrationId: ObjectIdString) {
  const trpc = useTRPC();

  const {
    data: statuses,
    isLoading,
    error,
  } = useQuery(trpc.configs.staffIq.getCandidateStatuses.queryOptions({ integrationId }));

  return {
    statuses: statuses ?? [],
    isLoading,
    error,
  };
}

export function useScrollControls(ref: RefObject<HTMLElement | null>) {
  const scrollToTop = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      ref.current?.scrollTo({ top: 0, behavior });
    },
    [ref]
  );

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      const el = ref.current;
      if (el) el.scrollTo({ top: el.scrollHeight, behavior });
    },
    [ref]
  );

  const scrollToElement = useCallback(
    (id: string, block: ScrollLogicalPosition = 'center', behavior: ScrollBehavior = 'auto') => {
      ref.current?.querySelector(`[data-scroll-id="${id}"]`)?.scrollIntoView({ behavior, block });
    },
    [ref]
  );

  return {
    scrollToTop,
    scrollToBottom,
    scrollToElement,
  };
}

interface UseCopyFromElementOptions {
  itemNameToToast?: string;
  // If true, only copy the inner HTML, e.g., blockquote content
  inner?: boolean;
}

/**
 * Hook for copying text content from a referenced DOM element.
 *
 * Provides:
 * - A 'handleCopy' function
 * - An 'isCopied' boolean state for temporary UI feedback (true for 2 seconds after copying)
 */
export function useCopyFromElement(
  elementRef: RefObject<HTMLElement | null>,
  options: UseCopyFromElementOptions = {}
) {
  const [isCopied, setIsCopied] = useState(false);
  const { addToast } = useContext(ToastContext);
  const { itemNameToToast, inner } = options;

  const handleCopy = useCallback(async () => {
    const element = elementRef.current;
    const success = await copyElement(element, { inner });

    if (isCopied) return;

    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);

    if (itemNameToToast && success && element) {
      addToast(`${itemNameToToast} "${element.textContent}" copied to clipboard`);
    }
  }, [addToast, elementRef, inner, isCopied, itemNameToToast]);

  return {
    handleCopy,
    isCopied,
  };
}

export function useToggle(initialValue: boolean = false) {
  const [value, setValue] = useState(initialValue);

  const toggleValue = () => {
    setValue((prev) => !prev);
  };

  return { value, setValue, toggleValue };
}

export function useSelectedValue<T>(initialValue: T) {
  const [value, setValue] = useState<T>(initialValue);

  return { value, setValue };
}

export function useSelectedValues<T>(initialValues: T[] = []) {
  const [values, setValues] = useState<T[]>(initialValues);

  const add = (newVal: T) => {
    setValues((prev) => {
      if (R.isIncludedIn(newVal, prev)) {
        return prev;
      }
      return [...prev, newVal];
    });
  };

  const remove = (valToRemove: T) => {
    setValues((prev) => prev.filter((v) => v !== valToRemove));
  };

  const removeBy = (predicate: (value: T) => boolean) => {
    setValues((prev) =>
      // filter out the values for which the predicateFn returns True
      prev.filter((v) => !predicate(v))
    );
  };

  const toggle = (val: T) => {
    setValues((prev) => {
      if (R.isIncludedIn(val, prev)) {
        // If present, remove it
        return prev.filter((v) => v !== val);
      } else {
        return [...prev, val];
      }
    });
  };

  const toggleAll = (vals: T[]) => {
    setValues((prev) => {
      if (R.isDeepEqual(prev, vals)) {
        return [];
      } else {
        return vals;
      }
    });
  };

  const clear = () => {
    setValues([]);
  };

  const has = (val: T) => {
    return R.isIncludedIn(val, values);
  };

  const hasAny = () => {
    return values.length > 0;
  };

  return { values, setValues, add, remove, removeBy, toggle, toggleAll, clear, has, hasAny };
}
